import React, { useLayoutEffect, useRef, useState } from "react";
import { DataGridTable, Spinner } from "gold-ui-kit";

// DataGridTable's built-in row-number header column plus its 1px borders.
const GRID_CHROME = 60 + 2;
// Room for the grid's own vertical scrollbar so the last column is not clipped.
const SCROLLBAR = 14;

/**
 * DataGridTable sizes columns in fixed px and reads widths only on mount, so this
 * measures the container, spreads the spare width over `flex` columns and remounts
 * the grid when the width changes. Height fills the viewport below the grid.
 */
const FitDataGrid = ({ columns, bottomOffset = 24, minHeight = 320, height, loading = false, ...gridProps }) => {
  const ref = useRef(null);
  const [box, setBox] = useState({ width: 0, height: minHeight });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const autoHeight = Math.max(minHeight, Math.floor(window.innerHeight - rect.top - bottomOffset));
      setBox((prev) =>
        Math.abs(prev.width - rect.width) < 1 && prev.height === autoHeight ? prev : { width: rect.width, height: autoHeight }
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [bottomOffset, minHeight]);

  const fixedTotal = columns.reduce((s, c) => s + (c.flex ? c.minWidth || 120 : c.width || 140), 0);
  const flexTotal = columns.reduce((s, c) => s + (c.flex || 0), 0);
  const spare = Math.max(0, box.width - GRID_CHROME - SCROLLBAR - fixedTotal);

  const sized = columns.map(({ flex, ...c }) =>
    flex ? { ...c, width: Math.floor((c.minWidth || 120) + (spare * flex) / flexTotal) } : c
  );

  return (
    <div ref={ref} className="relative w-full">
      {box.width > 0 && (
        <DataGridTable
          key={Math.round(box.width)}
          theme="modern"
          striped
          columns={sized}
          height={height ?? box.height}
          {...gridProps}
        />
      )}
      {/* The grid's own overlay has a hard-coded English "Loading…" label, so we draw our own. */}
      {loading && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/60">
          <Spinner size={28} label="Yükleniyor…" labelPosition="bottom" />
        </div>
      )}
    </div>
  );
};

export default FitDataGrid;
